import DataServiceSecure from '../modules/assurance/services/data-secure.service';
import UtilsService from './utils.service';
import templateCommon from '../app/helpers/template-common';
import moment from 'moment-timezone';
import _ from "lodash";
import LoginService from "./login.service";
import FilterExpressionService from '../modules/assurance/services/filter.expression.service';
import FormatterCommon from '../app/data-formatters/formatter-common';
import DataProvider from "../app/data-framework/data-provider";
import DomPurify from "dompurify";
import DocumentService from "../modules/admin/services/DocumentService";

// map of datasets with child datasets associated with them, e.g. "Meetings" has "Participants" as a sub-dataset
const CHILD_DATASET_MAP = {
  'meetings': 'participants'
}
const LINK_ICON = '<i class="fa fa-external-link fa-fw external-call-details-link-icon" aria-hidden="true"></i>';
const EDIT_ICON = '<i class="fal fa-edit fa-fw external-call-details-link-icon" aria-hidden="true"></i>';
const VIEW_ICON = '<i class="far fa-eye fa-fw external-call-details-link-icon" aria-hidden="true"></i>';

const PAST_ICON = '<i class="far fa-hourglass-half fa-fw status-icon" aria-hidden="true"></i>';
const STREAM_ICON = '<i class="far fa-water fa-fw status-icon" aria-hidden="true"></i>';

const METRIC_ICON = '<i class="far fa-chart-line fa-fw status-icon" aria-hidden="true"></i>';
const EVENT_ICON = '<i class="far fa-rectangle-list fa-fw status-icon" aria-hidden="true"></i>';

const AUDIO_ICON = '<i class="fal fa-volume fa-fw status-icon" aria-hidden="true"></i>';
const VIDEO_ICON = '<i class="fal fa-video fa-fw status-icon" aria-hidden="true"></i>';
const PRESENTATION_ICON = '<i class="fal fa-presentation-screen fa-fw status-icon" aria-hidden="true"></i>';

const TX_ICON = '<i class="fal fa-arrow-up-from-bracket fa-fw status-icon" aria-hidden="true"></i>';
const RX_ICON = '<i class="fal fa-arrow-down-to-bracket fa-fw status-icon" aria-hidden="true"></i>';

const CRITICAL_ICON = '<i class="fas fa-exclamation-triangle fa-fw status-icon" aria-hidden="true"></i>';
const WARN_ICON = '<i class="fas fa-exclamation-square fa-fw status-icon" aria-hidden="true"></i>';
const INFO_ICON = '<i class="fas fa-info-circle fa-fw status-icon" aria-hidden="true"></i>';
const CLEAR_ICON = '<i class="fas fa-check-circle fa-fw status-icon" aria-hidden="true"></i>';
const QUESTION_ICON = '<i class="fas fa-question-circle fa-fw status-icon" aria-hidden="true"></i>';

const SCORE_MAX_ICON = '<i class="fas fa-circle fa-fw status-icon" aria-hidden="true"></i>';
const SCORE_HIGH_ICON = '<i class="far fa-circle-three-quarters-stroke fa-fw status-icon" aria-hidden="true"></i>';
const SCORE_MEDIUM_ICON = '<i class="far fa-circle-half-stroke fa-fw status-icon" aria-hidden="true"></i>';
const SCORE_LOW_ICON = '<i class="fa-sharp far fa-circle-quarter-stroke fa-fw status-icon" aria-hidden="true"></i>';
const SCORE_MIN_ICON = '<i class="fal fa-circle-dashed fa-fw status-icon" aria-hidden="true"></i>';


const USER_ICON = '<i class="fas fa-user fa-fw status-icon" aria-hidden="true"></i>';
const USERS_ICON = '<i class="fas fa-users fa-fw status-icon" aria-hidden="true"></i>';

const ENABLED_ICON = '<i class="far fa-toggle-large-on fa-fw toggle-on-icon" aria-hidden="true"></i>';
const DISABLED_ICON = '<i class="far fa-toggle-large-off fa-fw toggle-off-icon" aria-hidden="true"></i>';

function formatNumber(num) {
  return _.toNumber(num).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatInteger(num) {
  return _.toNumber(num).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function normalizeValue(value) {
  if (value === null || value === undefined) value = '';
  return value;
}

// All cell contents should be wrapped in this div for CSS styling
function applyCellContentWrapper(content) {
  content = normalizeValue(content);
  return `<div class="cell-content-wrapper">${content}</div>`;
}

function defaultFormatter(cell) {
  let value = normalizeValue(cell.getValue());
  return applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(value, { title: value })); // todo sanitize
}

function htmlFormatter(cell) {
  let value = _.unescape(cell.getValue());
  return applyCellContentWrapper(value);
}

function metricDisplayFormatter(cell) {
  let value = cell.getValue();
  let data = cell.getData();

  value = normalizeValue(value);
  if (value) {
    value = (data.cdr ? PAST_ICON : STREAM_ICON) + value;
  }
  return applyCellContentWrapper(value);
}
function categoryDisplayFormatter(cell) {
  let value = cell.getValue();
  let data = cell.getData();

  value = normalizeValue(value);
  if (value) {
    value = (data.event ? EVENT_ICON : METRIC_ICON) + value;
  }
  return applyCellContentWrapper(value);
}

function editableEnabledFormatter(cell) {
  let value = cell.getValue();
  let html = '                  <label align="left" class="legend-switch legend-switch-small">\n'
            + '                    <input id="enabled" type="checkbox" ' + ( value ? 'checked' : '' ) + '>\n'
            + '                    <span class="legend-slider legend-slider-small"></span>\n'
            + '                  </label>\n';

  return applyCellContentWrapper(html);
}

function enabledFormatter(cell) {
  let value = cell.getValue();

  return applyCellContentWrapper(value ? ENABLED_ICON : DISABLED_ICON);
}

function createdByDisplayFormatter(cell) {
  let value = cell.getValue();
  let data = cell.getData();

  value = (data.createdBy === LoginService.userObject.userId ? USER_ICON : USERS_ICON) + normalizeValue(value);
  return applyCellContentWrapper(value);
}

/*
    Most formatters adapted from dv-grid.service.js
    TODO: Safe to remove? No "SEMANTIC_TEXT" columns in CPMM as of 10/7/21. CPMA may still use it.
*/
function semanticTextFormatter(cell) {
  let status = cell.getValue(),
      cls = 'semantic-text iconpanel',
      iconCls = 'flaticon-circle111';

  if (typeof status === 'string') {
    switch (status.toUpperCase()) {
      case 'OK':
      case 'AVAILABLE':
        cls += ' semantic-good';
        iconCls = 'fas fa-check-circle'
        break;
      case 'GOOD':
        cls += ' semantic-good'; break;
      case 'FAIR':
        cls += ' semantic-fair'; break;
      case 'BAD':
        cls += ' semantic-bad'; break;
      case 'NOTAVAILABLE':
        cls += ' semantic-bad';
        iconCls = 'fas fa-times-circle';
        break;
      case 'ERROR':
        cls += ' semantic-bad';
        iconCls = 'fas fa-exclamation-circle';
        break;
      case 'DOWN':
        cls += ' semantic-bad';
        iconCls = 'fas fa-arrow-circle-down';
        break;
      case 'NONE':
        iconCls = 'fas fa-minus-circle'
        cls += ' semantic-none';
        break;
      case 'UNKNOWN':
        cls += ' semantic-unknown'; break;
      case 'UNREACHABLE':
        iconCls = 'fas fa-times-circle'
        cls += ' semantic-fair';
        break;
      case 'INCALL':
        cls += ' semantic-incall';
        iconCls = 'fas fa-phone';
        break;
      default:
        cls += ' semantic-unknown';
    }
  }

  return applyCellContentWrapper(`<span class="${cls}"><i class="${iconCls}"></i> ${status}</span>`);
}

function qualityFormatter(cell) {
  let quality = cell.getValue() || '',
    cls = 'semantic-text iconpanel',
    iconCls = '';

  if (typeof quality === 'string') {
    switch (quality.toUpperCase()) {
      case 'GOOD':
        cls += ' semantic-good';
        iconCls = 'fas fa-check-circle';
        break;
      case 'FAIR':
        cls += ' semantic-fair';
        iconCls = 'fas fa-minus-circle';
        break;
      case 'BAD':
        cls += ' semantic-bad';
        iconCls = 'fas fa-exclamation-circle';
        break;
      case 'UNKNOWN':
        cls += ' semantic-neutral';
        iconCls = 'fas fa-question-circle';
        break;
      default:
        cls += ' semantic-unknown';
    }
  }

  return applyCellContentWrapper(`<span class="${cls}"><i class="${iconCls}"></i> ${quality}</span>`);
}

function eventLevelFormatter(cell) {
  let quality = cell.getValue(),
    cls = 'semantic-text iconpanel',
    iconCls = '';

  if (typeof quality === 'string') {
    switch (quality.toUpperCase()) {
      case 'CRITICAL':
        cls += ' semantic-critical';
        iconCls = 'fas fa-triangle-exclamation';
        break;
      case 'ERROR':
        cls += ' semantic-bad';
        iconCls = 'fas fa-circle-xmark';
        break;
      case 'WARNING':
        cls += ' semantic-fair';
        iconCls = 'fas fa-square-bolt';
        break
      case 'INFO':
        cls += ' semantic-incall';
        iconCls = 'fas fa-circle-info';
        break;
      case 'DEBUG':
        cls += ' semantic-custom';
        iconCls = 'fas fa-bug';
        break;
      case 'NONE':
        cls += ' semantic-none';
        iconCls = 'fas fa-circle-question';
        break;
      default:
        cls += ' semantic-unknown';
    }
  }

  return applyCellContentWrapper(`<span class="${cls}"><i class="${iconCls}"></i> ${quality}</span>`);
}


function streamTypeFormatter(cell) {
  let base = normalizeValue(cell.getValue());
  let value = base;
  
  if (base && base.includes('Audio')) {
    value = AUDIO_ICON + 'Audio';
  }
  else if (base && base.includes('Video')) {
    value = VIDEO_ICON + 'Video';
  }
  else if (base && base.includes('Presentation')) {
    value = PRESENTATION_ICON + 'Presentation';
  }

  if (base && base.includes('Tx')) {
    value += ' <br>' + TX_ICON + 'Tx';
  }
  else if (base && base.includes('Rx')) {
    value += ' <br>' + RX_ICON + 'Rx';
  }


  return applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(value, { title: value }));
}


function adrStatusFormatter(cell) {
  let quality = cell.getValue(),
    cls = 'semantic-text iconpanel',
    iconCls = '';

  if (typeof quality === 'string') {
    switch (quality.toLowerCase()) {
      case 'file':
        cls += ' semantic-none';
        iconCls = 'fas fa-file';
        break;
      case 'uploaded':
        cls += ' semantic-incall';
        iconCls = 'fas fa-circle-arrow-up';
        break;
      case 'downloading':
        cls += ' semantic-incall';
        iconCls = 'fas fa-circle-arrow-down';
        break;
      case 'pending':
        cls += ' semantic-incall';
        iconCls = 'fas fa-arrow-progress';
        break;
      case 'success':
        cls += ' semantic-good';
        iconCls = 'fas fa-circle-check';
        break;
      case 'empty':
        cls += ' semantic-unknown';
        iconCls = 'far fa-file';
        break;
      case 'ignored':
        cls += ' semantic-unknown';
        iconCls = 'far fa-circle';
        break;
      case 'warn':
        cls += ' semantic-fair';
        iconCls = 'fas fa-square-bolt';
        break;
      case 'error':
        cls += ' semantic-bad';
        iconCls = 'fas fa-circle-xmark';
        break;
      case 'xerror':
        cls += ' semantic-critical';
        iconCls = 'fas fa-triangle-exclamation';
        break;
      default:
        cls += ' semantic-unknown';
    }
  }

  return applyCellContentWrapper(`<span class="${cls}"><i class="${iconCls}"></i> ${quality}</span>`);
}

function statusFormatter(cell) {
  let status = cell.getValue(),
    cls = 'semantic-text iconpanel',
    iconCls = '';

  if (typeof status === 'string') {
    switch (status.toUpperCase()) {
      case 'OK':
      case 'AVAILABLE':
        cls += ' semantic-good';
        iconCls = 'fas fa-check-circle'
        break;
      case 'NOTAVAILABLE':
      case 'ERROR':
        cls += ' semantic-bad';
        iconCls = 'fas fa-exclamation-circle';
        break;
      case 'NONE':
        iconCls = 'fas fa-question-circle'
        cls += ' semantic-neutral';
        break;
      case 'UNREACHABLE':
        iconCls = 'fas fa-times-circle'
        cls += ' semantic-fair';
        break;
      case 'INCALL':
        cls += ' semantic-incall';
        iconCls = 'fas fa-phone';
        break;
      default:
        cls += ' semantic-unknown';
    }
  }

  return applyCellContentWrapper(`<span class="${cls}"><i class="${iconCls}"></i> ${status}</span>`);
}

function callsLinkFormatter(cell) {
  let endpointId = cell.getData().endpoint_id;
  if (endpointId && endpointId > 0) {
    let content = 'Calls';
    return applyCellContentWrapper(templateCommon.getHtmlTagWithContentAndAttrs('a', content, {
      href: '#monitoring/calls?filter=endpoint_id==&quot;' + endpointId + '&quot;',
    }));
  } else {
    return '';
  }
}

function callDetailsFormatter(cell) {
  let callId = cell.getValue();
  let rowData = cell.getData();
  let startTime = rowData.start_time;
  let qualityDetailsUpdated = rowData.quality_details_updated;
  let callRecordsUpdated = rowData.call_records_updated;
  if (startTime && callId && (qualityDetailsUpdated || callRecordsUpdated)) {
    let content = 'Summary';
    if (qualityDetailsUpdated) {
      content = 'Quality';
    }
    if (callRecordsUpdated) {
      if (qualityDetailsUpdated) {
        content += '/Record';
      } else {
        content = 'Record';
      }
    }

    return applyCellContentWrapper(templateCommon.getHtmlTagWithContentAndAttrs('a', content, {
      href: '#monitoring/calls/' + startTime + '/' + callId
    }));
  } else {
    return '';
  }
}

function alertDetailsEditFormatter(cell) {
  let id = cell.getValue();
  if (id) {
    return applyCellContentWrapper(templateCommon.getHtmlTagWithContentAndAttrs('a', EDIT_ICON, {
      href: '#monitoring/moi/' +  id + '?edit'
    }));
  } else {
    return '';
  }
}

function alertDetailsViewFormatter(cell) {
  let content = cell.getValue();
  let rowData = cell.getData();
  let id = rowData.alertId;

  if (content) {
  } else {
    content = VIEW_ICON;
  }
  return applyCellContentWrapper(templateCommon.getHtmlTagWithContentAndAttrs('a', content, {
    href: '#monitoring/moi/' +  id
  }));
}

function nameCallDetailsFormatter(cell) {
  let name = cell.getValue();
  let callId = cell.getData().participant_id;
  let rowData = cell.getData();
  let startTime = rowData.start_time;
  let qualityDetailsUpdated = rowData.quality_details_updated;
  let callRecordsUpdated = rowData.call_records_updated;
  if (startTime && callId && (qualityDetailsUpdated || callRecordsUpdated)) {
    let content = name;
    return applyCellContentWrapper(templateCommon.getHtmlTagWithContentAndAttrs('a', content, {
      href: '#monitoring/calls/' + startTime + '/' + callId,
    }));
  } else {
    return defaultFormatter(cell);
  }
}

function groupNiceNameSorter(a, b, aRow, bRow, column, dir, sorterParams) {
  function defaultStringSort(a, b, aRow, bRow, column, dir, params){
    var alignEmptyValues = params.alignEmptyValues;
    var emptyAlign = 0;
    var locale;

    //handle empty values
    if(!a){
      emptyAlign =  !b ? 0 : -1;
    }else if(!b){
      emptyAlign =  1;
    }else{
      //compare valid values
      switch(typeof params.locale){
        case "boolean":
          if(params.locale){
            locale = this.langLocale();
          }
          break;
        case "string":
          locale = params.locale;
          break;
      }

      return String(a).toLowerCase().localeCompare(String(b).toLowerCase(), locale);
    }

    //fix empty values in position
    if((alignEmptyValues === "top" && dir === "desc") || (alignEmptyValues === "bottom" && dir === "asc")){
      emptyAlign *= -1;
    }

    return emptyAlign;
  }

  function safeVal(ar, i) {
    if (ar[i]) return ar[i][3] || ar[i][1];
    return '';
  }

  let results = 0;
  let index = 0;

  while (results === 0 && (safeVal(a,index) || safeVal(b,index))) {
    results = defaultStringSort(safeVal(a,index), safeVal(b,index), aRow, bRow, column, dir, sorterParams);
    index++;
  }

  return results;
}

function groupNiceNameFormatter(cell) {
  function findType(group) {
    if (group) {
      let found = group.find(g => {
        return g[0] === 'source_type';
      });
      if (found) {
        return found[1];
      }
    }
    return undefined;
  }
  moment.tz.setDefault(moment.tz.guess());
  let group = cell.getValue();
  let name = group.map(g => {
    let v = '';
    if (g[3]) {
      // if call_id or start_time
      v = g[3].replace(/{{time:(\d+)}}/, (match, p1) => moment.utc(Number(p1)).local().format('DD MMM YYYY - HH:mm:ss'));
    } else {
      v = normalizeValue(g[1]);
    }
    v = DomPurify.sanitize(v);
    let gElement = g[0];
    
    if ('source_id' === gElement) {
      let type = findType(group);
      if (type) {
        if ('meetings' === type) {
          gElement = 'meeting_id';
        } else if ('systems' === type) {
          gElement = 'system_id';
        } else if ('endpoints' === type) {
          gElement = 'endpoint_id';
        } else if ('trunks' === type) {
          gElement = 'trunk_id';
        }
      }
    }
    if ('call_id' === gElement) {
      let content = v;
      v = templateCommon.getHtmlTagWithContentAndAttrs('a', content, {
        href: '#monitoring/calls/' + g[1]
      });
    } else if ('meeting_id' === gElement) {
      let ids = g[1].split('|');
      v = templateCommon.getHtmlTagWithContentAndAttrs('a', v, {
        href: '#monitoring/meetings?filter={}'.replace('{}', encodeURIComponent('meeting_id==' + ids[0])), // todo add start time to filter
      });
    } else if ('system_id' === gElement) {
      v = templateCommon.getHtmlTagWithContentAndAttrs('a', v, {
        href: '#monitoring/infrastructure?filter={}'.replace('{}', encodeURIComponent('system_id==' + g[1])),
      });
    } else if ('endpoint_id' === gElement) {
      v = templateCommon.getHtmlTagWithContentAndAttrs('a', v, {
        href: '#monitoring/endpoint/' + g[1] + '/tabid/0',
      });
    } else if ('trunk_id' === gElement) {
      let ids = g[1].split('|');
      v = templateCommon.getHtmlTagWithContentAndAttrs('a', v, {
        // href: '#monitoring/trunks?filter={}'.replace('{}', encodeURIComponent('trunk_id==' + ids[1])), // todo add system id to filter
        href: '#monitoring/trunks?filter={}'.replace('{}', encodeURIComponent('((trunk_id==' + ids[1] + ');(system_id==' + ids[0] +'))')),
      });
    } else if ('unique_message' === gElement && DataProvider.getInstance().isInternalUser() && v !== g[1]) { // if the message and unique_message are different
      v = templateCommon.getSpanWithContentAndAttrs(FormatterCommon.sanitize(v), { title: FormatterCommon.sanitize(normalizeValue(g[1])), style:'color: #1d4594' });
    } else if ('event_type' === gElement) {
      v = _.startCase(v); // todo look up name from id in metadata known values
    }
    return v;
  }).join(', ');

  return `<div class="cell-content-wrapper">${name}</div>`;
}

function eventMessageFormatter(cell) {
  let v = cell.getValue();
  let data = cell.getData();
  let uniqueMessage = data.unique_message;
  let name = templateCommon.getSpanWithContentAndAttrs(FormatterCommon.sanitize(v), { title: FormatterCommon.sanitize(normalizeValue(v)) });

  if (DataProvider.getInstance().isInternalUser() && v !== uniqueMessage) { // if the message and unique_message are different
    name = templateCommon.getSpanWithContentAndAttrs(FormatterCommon.sanitize(v), { title: FormatterCommon.sanitize(normalizeValue(uniqueMessage)), style:'color: #1d4594' });
  }
  return `<div class="cell-content-wrapper">${name}</div>`;
}

function dateFormatter(cell) {
  let renderedDatetime = '';
  let value = cell.getValue();

  if (!!value) {
    let datetime = moment.utc(value, 'YYYY-MM-DD');
    let formattedDatetime = datetime.format('DD MMM YYYY');
    renderedDatetime = applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(formattedDatetime, { title: formattedDatetime }));
  }

  return renderedDatetime;
}

function datetimeFormatter(cell) {
  let renderedDatetime = '';
  let value = cell.getValue();

  if (!!value) {
    if (!value.endsWith('Z')) { // currently a hack for LocalDateTime adr data
      value += 'Z';
    }
    moment.tz.setDefault(moment.tz.guess());
    let datetime = moment(value, 'YYYY-MM-DDTHH:mm:ss.SSSZ');
    let formattedDatetime = datetime.format('DD MMM YYYY - HH:mm:ss');
    renderedDatetime = applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(formattedDatetime, { title: formattedDatetime }));
  }

  return renderedDatetime;
}

function datetimeSmallFormatter(cell) {
  let renderedDatetime = '';
  let value = cell.getValue();

  if (!!value) {
    moment.tz.setDefault(moment.tz.guess());
    let datetime = moment(value, 'YYYY-MM-DDTHH:mm:ss.SSSZ');
    let formattedDatetime = datetime.format('DD MMM YYYY' + '<br>' + 'HH:mm:ss');
    let formattedDatetime2 = datetime.format('DD MMM YYYY - HH:mm:ss');
    renderedDatetime = applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(formattedDatetime, { title: formattedDatetime2 }));
  }

  return renderedDatetime;
}

function endDateFormatter(cell) {
  let renderedDatetime = dateFormatter(cell);
  if (!renderedDatetime) {
    let span = templateCommon.getSpanWithContentAndAttrs('In Progress', { title: 'In Progress', class: 'call-list-endtime-inprogress' });
    renderedDatetime = applyCellContentWrapper(templateCommon.getDivWithContentAndAttrs(span, { class: 'call-list-endtime-inprogress-container' }));
    cell.getElement().style.padding = 0; // hack to shrink the cell size in this special case
  }
  return renderedDatetime;
}

function endDatetimeFormatter(cell) {
  let renderedDatetime = datetimeFormatter(cell);
  if (!renderedDatetime) {
    let span = templateCommon.getSpanWithContentAndAttrs('In Progress', { title: 'In Progress', class: 'call-list-endtime-inprogress' });
    renderedDatetime = applyCellContentWrapper(templateCommon.getDivWithContentAndAttrs(span, { class: 'call-list-endtime-inprogress-container' }));
    cell.getElement().style.padding = 0; // hack to shrink the cell size in this special case
  }
  return renderedDatetime;
}

function endTimeSmallFormatter(cell) {
  let renderedDatetime = datetimeSmallFormatter(cell);
  if (!renderedDatetime) {
    let span = templateCommon.getSpanWithContentAndAttrs('In Progress', { title: 'In Progress', class: 'call-list-endtime-inprogress small-badge' });
    renderedDatetime = applyCellContentWrapper(templateCommon.getDivWithContentAndAttrs(span, { class: 'call-list-endtime-inprogress-container' }));
  }
  return renderedDatetime;
}

function meetingLinkFormatter(cell) {
  let data = cell.getData();
  let startTime = data.meeting_start_time || '';
  let name = data.meeting_name || '';
  let id = data.meeting_id || '';

  if (startTime && id) {
    let filter = encodeURIComponent('meeting_id==' + id);
    return applyCellContentWrapper(templateCommon.getHtmlTagWithContentAndAttrs('a', name, {
      href: '#monitoring/meetings?filter=' + filter,
      title: name,
    }));
  }
  else {
    return '';
  }
}

function adrDocumentLinkFormatter(cell) {
  let value = cell.getValue();
  let data = cell.getData();
  let docId = data['adr_LastDoc.DocId'] || data.id;
  let content = value;
  if (window.Config.adrDownload && content && docId) {
    return applyCellContentWrapper(templateCommon.getHtmlTagWithContentAndAttrs('a', content));
  } else {
    return applyCellContentWrapper(content);
  }
}

function endpointLinkFormatter(cell) {
  //the 'toString' is to deal with a QA system with endpoint_id === -1.
  //the isNil check is to avoid a null pointer error when calling .toString()
  let value = cell.getValue();
  let data = cell.getData();
  let endpointName = data.endpoint_name;
  let endpointId = data.endpoint_id;
  let content = (endpointName) ? endpointName : _.isNil(value) ? value : value.toString();
  if (content && endpointId) {
    return applyCellContentWrapper(templateCommon.getHtmlTagWithContentAndAttrs('a', content, {
      href: '#monitoring/endpoint/' + endpointId + '/tabid/0',
    }));
  } else {
    return applyCellContentWrapper(content);
  }
}

function networkLinkFormatter(cell) {
  //the 'toString' is to deal with a QA system with endpoint_id === -1.
  //the isNil check is to avoid a null pointer error when calling .toString()
  let value = cell.getValue();
  let data = cell.getData();
  let endpointName = data['dimipsubnet.networkname'];
  let endpointId = data['dimipsubnet.dimipsubnetsid'];
  let start = data['fctnetworkinsights.targetstartdate'];
  let end = data['fctnetworkinsights.targetenddate'];
  let content = (endpointName) ? endpointName : _.isNil(value) ? value : value.toString();
  if (content && endpointId) {
    return applyCellContentWrapper(templateCommon.getHtmlTagWithContentAndAttrs('a', content, {
      href: '#historical2/networks/tabid/0/params'+'/inpstartdate|' + start + '&inpenddate|' + end +'&wherecondition%7C%5B%7B%22selectedConjunction%22%3A%22AND%22%2C%22conditions%22%3A%5B%7B%22id%22%3A1%2C%22selectedLhs%22%3A%22dimipsubnet.dimipsubnetsid%22%2C%22selectedOperator%22%3A%22%3D%22%2C%22rhsText%22%3A%22' + endpointId + '%22%2C%22rhsCount%22%3A%22%22%2C%22listtype%22%3Afalse%2C%22valueType%22%3A%22numeric%22%2C%22selectedRhs%22%3A%5B%5D%2C%22conjunctionVisible%22%3Afalse%2C%22hidden%22%3Afalse%2C'+
        '%22selectedLhsTitle%22%3A%22Network%20Id%22%2C'+
        '%22optitle%22%3A%22equals%22%2C%22desc%22%3A%22Network%20Id%2520equals%2520' + endpointId + '%22%7D%5D%7D%5D',
    })); // todo fix filter
  } else {
    return applyCellContentWrapper(content);
  }
}

function eventOriginNameFormatter(cell) {
  let value = cell.getValue();
  let data = cell.getData();
  let content = value;
  let id = data.source_id;
  let type = data.source_type;
  if (type === 'endpoints' && id && id > 0) {
    return applyCellContentWrapper(templateCommon.getHtmlTagWithContentAndAttrs('a', content, {
      href: '#monitoring/endpoint/' + id + '/tabid/0',
    }));
  } else if (type === 'systems' && id && id > 0) {
    return applyCellContentWrapper(templateCommon.getHtmlTagWithContentAndAttrs('a', content, {
      href: '#monitoring/infrastructure?filter={}'.replace('{}', encodeURIComponent('system_id==' + id)),
    }));
  } else {
    return applyCellContentWrapper(content);
  }
}

function linkFormatter(cell) {
  let value = cell.getValue();
  if (value) {
    return applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(value, { class: 'dataset-link-text' }));
  } else {
    return applyCellContentWrapper(value);
  }
}

function hostAddressFormatter(cell) {
  let value = cell.getValue();
  let content = _.isNil(value) ? value : _.escape(value.toString());
  if (!_.isEmpty(value)) {
    content += LINK_ICON;

    return applyCellContentWrapper(templateCommon.getHtmlTagWithContentAndAttrs('a', content, {
      href: 'https://' + value,
      title: value,
      target: '_blank'
    }));
  }
  return '';
}

function durationFormatter(cell) {
  let value = cell.getValue();
  try {
    if (_.isFinite(value) && value >= 0) {
      return applyCellContentWrapper(UtilsService.getDurationDisplayTextFromSeconds(value));
    }
  } catch (e) {}

  return '';
}

function scoreFormatter(cell) {
  let value = parseFloat(cell.getValue());
  let data = cell.getData();
  let severity = data['fctnetworkinsightsxml.severity'] || '';
  value = _.isFinite(value) ? formatNumber(value) : '';


  let cls = '';

  switch (severity.toUpperCase()) {
    case 'CRITICAL':
      cls += ' semantic-critical';
      break;
    case 'ERROR':
      cls += ' semantic-bad';
      break;
    case 'WARNING':
      cls += ' semantic-fair';
      break
    case 'INFO':
      cls += ' semantic-incall';
      break;
    case 'DEBUG':
      cls += ' semantic-custom';
      break;
    case 'NONE':
      cls += ' semantic-none';
      break;
    default:
      cls += ' semantic-unknown';
  }

  if (value) {
    value = normalizeValue(value);
    value += (value < 13 ? SCORE_MIN_ICON : value < 38 ? SCORE_LOW_ICON : value < 63 ? SCORE_MEDIUM_ICON : value < 88 ? SCORE_HIGH_ICON : SCORE_MAX_ICON);
  }
  
  return applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(value, { title: value, class: cls }));
}

function integerFormatter(cell) {
  let value = parseFloat(cell.getValue());
  value = _.isFinite(value) ? formatInteger(value) : '';
  return applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(value, { title: value }));
}

function numberFormatter(cell) {
  let value = parseFloat(cell.getValue());
  value = _.isFinite(value) ? formatNumber(value) : '';
  return applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(value, { title: value }));
}

function bytesFormatter(cell) {
  let value = parseFloat(cell.getValue());
  value = _.isFinite(value) ? formatBytes(value) : '';
  return applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(value, { title: value }));
}

function formatBytes(bytes) {
  let byteUnits = [' bytes', ' KB', ' MB', ' GB', ' TB', 'PB', 'EB', 'ZB', 'YB'];
  if (bytes < 1000) {
    return bytes + byteUnits[0];
  }
  var i = 0;
  while (bytes >= 1000 && i < byteUnits.length) {
    bytes = bytes / 1000;
    i++;
  }

  return Math.max(bytes, 0).toFixed(Number.isInteger(bytes) ? 0 : 1) + byteUnits[i];
}


function mosFormatter(cell) {
  let value = cell.getValue();
  let num = parseFloat(value);
  if (num === -2.0) {
    return applyCellContentWrapper('Invalid');
  } else if (num === -1.0) {
    return applyCellContentWrapper('');
  }
  return applyCellContentWrapper(value);
}

function percentageFormatter(cell) {
  let rawvalue = parseFloat(cell.getValue());
  let value = _.isFinite(rawvalue) ? formatNumber(rawvalue) + '%' : '';
  rawvalue = _.isFinite(rawvalue) ? rawvalue + '%' : '';
  return applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(value, { title: rawvalue }));
}

function bitrateFormatter(cell) {
  let value = parseFloat(cell.getValue());
  value = _.isFinite(value) ?
    formatNumber(value / 1000) + ' kbps' : '';

  return applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(value, { title: value }));
}

function mediaRoutedFormatter(cell) {
  let value = cell.getValue();
  let dataSource = cell.getData().data_source;
  let text = value ? 'Yes' : 'No';

  text = (dataSource.toLowerCase() === 'vcs' ||
          dataSource.toLowerCase() === 'vcsx' ||
          dataSource.toLowerCase() === 'vcsc') ?
          text : 'N/A';

  return applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(text, { title: text }));
}

function booleanYesNoFormatter(cell) {
  let value = cell.getValue();

  let text;
  if (typeof value === 'string') {
    value = value.toLowerCase();
  }

  if (value === 'yes' || value === 'y' || value === true || value === 'true' || value === 1 || value === '1') {
    text = 'Yes';
  } else if (value === 'no' || value === 'n' || value === false || value === 'false' || value === 0 || value === '0') {
    text = 'No';
  } else if (value === 'n/a') {
    text = 'N/A';
  } else {
    text = '';
  }
  return applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(text, { title: text }));
}

function commaSeparatedListFormatter(cell) {
  let value = cell.getValue();
  let values = value ? value.split(',') : [];
  let result = '';
  values.forEach(val => {
    if (result) { result += ', '; }
    result += val;
  });
  return applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(result, { title: result }));
}

function callTypeFormatter(cell) {
  let value = cell.getValue();
  let icon;
  if (value) {
    icon = {
      audio: '<i class="far fa-volume peripherals-icon"></i> ',
      video: '<i class="far fa-video peripherals-icon"></i> '
    }[value.toLowerCase()];
  }
  if (!icon) icon = '';

  value = normalizeValue(value);
  return applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(icon + value, { title: value }));
}

function connectionFormatter(cell) {
  let value = cell.getValue();
  let icon;
  if (value) {
    icon = {
      wifi: '<i class="fas fa-wifi peripherals-icon"></i> ',
      wired: '<i class="fal fa-network-wired fa-flip-vertical peripherals-icon"></i> ',
      ethernet: '<i class="fal fa-network-wired fa-flip-vertical peripherals-icon"></i> ',
      mobile: '<i class="fal fa-tower-broadcast peripherals-icon"></i> ',
      "3g": '<i class="fal fa-tower-broadcast peripherals-icon"></i> '
    }[value.toLowerCase()];
  }
  if (!icon) icon = '';

  value = normalizeValue(value);
  return applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(icon + value, { title: value }));
}

function multiFormatter(cell, fields, prefixText, postfixText, prefixHtml) {
  var value = '';
  var title = '';
  fields.forEach((field, i) => {
    let fieldVal = cell.getData()[field];
    if (fieldVal) {
      fieldVal = prefixText[i] + fieldVal + postfixText[i];
    }
    if (value) {
      value += '<br>';
      title += ' - ';
    }
    value += fieldVal ? prefixHtml[i] + fieldVal : '';
    title += fieldVal ? fieldVal : '';

  });

  return applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(value, {title: title}));
}

function multiIPFormatter(cell) {
  return multiFormatter(cell,
      ['public_ip', 'local_ip'],
      ['', ''],
      [' (Public)', ' (Local)'],
      ['', '']);

}

function multiPlatformFormatter(cell) {
  return multiFormatter(cell,
      ['device_platform', 'device_version'],
      ['', ''],
      ['', ''],
      ['', '']);
}

function multiPeripheralsFormatter(cell) {
  return multiFormatter(cell,
      ['camera', 'microphone', 'speaker'],
      ['', '', ''],
      ['', '', ''],
      ['<i class="fal fa-video peripherals-icon"></i>',
        '<i class="fal fa-microphone-stand peripherals-icon"></i>',
        '<i class="fal fa-speakers peripherals-icon"></i>']);
}

function peripheralsFormatter(cell) {
  let value = cell.getValue();
  let icon;
  if (value) {
    icon = {
      camera: '<i class="fal fa-video peripherals-icon"></i>',
      microphone: '<i class="fal fa-microphone-stand peripherals-icon"></i>',
      speaker: '<i class="fal fa-speakers peripherals-icon"></i>'
    }[cell.getField()];
  }
  if (!icon) icon = '';

  value = normalizeValue(value);
  return applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(icon + value, { title: value }));
}

function starRatingFormatter(cell) {
  let value = cell.getValue();
  let icon = '<span class="star-feedback">☆☆☆☆☆</span>';
  if (value > 0 && value <= 5) {
    icon = [
      '<span class="star-feedback">☆☆☆☆☆</span>',
      '<span class="star-feedback">★☆☆☆☆</span>',
      '<span class="star-feedback">★★☆☆☆</span>',
      '<span class="star-feedback">★★★☆☆</span>',
      '<span class="star-feedback">★★★★☆</span>',
      '<span class="star-feedback">★★★★★</span>',
    ][value];
  }
  value = normalizeValue(value);
  return applyCellContentWrapper(templateCommon.getSpanWithContentAndAttrs(icon, { title: value }));
}

function alertStateFormatter(cell) {
  let value = cell.getValue() || '';
  let cls = 'alert-state-';
  let newVal = '';

  switch (value.toLowerCase()) {
    case 'clear':
      cls += 'ok';
      newVal = CLEAR_ICON;
      break;
    case 'critical':
      cls += 'error';
      newVal = CRITICAL_ICON;
      break;
    case 'warn': // TODO: What is the status from the server for warn?
      cls += 'warn';
      newVal = WARN_ICON;
      break;
    case 'info':
      cls += 'info';
      newVal = INFO_ICON;
      break;
    default:
      cls += 'unknown';
      newVal = QUESTION_ICON;
      break;
  }

  newVal += normalizeValue(value);

  let span = templateCommon.getSpanWithContentAndAttrs(newVal, { title: value });
  let div = applyCellContentWrapper(templateCommon.getDivWithContentAndAttrs(span, { class: 'alert-state-container ' + cls }));

  return div;
}


export default {
  convertColumns: function(columns, formatterParams, readOnly, dataTable) {
    let tabulatorColumns = [];

    columns.forEach(col => {
      let newCol = {
        field: col.displayId || col.id,
        title: col.name,
        minWidth: col.minWidth || 100,
        visible: !col.invisible,
        headerSort: !col.suppressSorting,
        formatterParams: formatterParams,
        description: col.description
      }
      if (col.notShown !== undefined) {
        newCol.notShown = col.notShown;
      }
      if (col.sort) {
        newCol.sort = col.sort;
      }
      // we don't want to default this to false as it interfears with the tooltipsHeader config, so only set when it exists in the metadata
      if (col.tooltip) {
        newCol.tooltip = col.tooltip;
      }
      if (col.contextMenu) { // gigantic hack to add context menu for ADR dataset
        let contextMenu = {
          'last-documents': {
            metadata: [
              {
                label: "Edit Metadata",
                action: function (e, cell) {
                  if (dataTable) {
                    dataTable.$emit('edit-metadata', cell.getRow(), dataTable.reloadDataFunc);
                  }
                }
              },
              {
                label: "Reset",
                action: async function (e, cell) {
                  let response = await DocumentService.resetMetadata(cell.getData()['adr_LastDoc.DocId']);
                  if (response && response.status === 200) {
                    dataTable.reloadDataFunc();
                  } else {
                  // todo
                    if (response && response.data && response.data.detail) {
                      // this.error = response.data.detail;
                      console.log(response.data.detail);
                    } else {
                      // this.error = 'Error saving ' + this.objectName;
                      console.log('Error resetting metadata ' + this.objectName);
                    }
                    // todo show error message
                  }
                }
              },
            ]
          }
        };
        newCol.contextMenu = contextMenu['last-documents']['metadata'];
      }

      switch (col.type) {
        case 'BITRATE':
          newCol.formatter = bitrateFormatter; break;
        case 'BOOLEAN_Y_N':
          newCol.formatter = booleanYesNoFormatter; break;
        case 'NAME_CALL_DETAILS_LINK':
          newCol.formatter = nameCallDetailsFormatter; break;
        case 'CALL_DETAILS_LINK':
          newCol.formatter = callDetailsFormatter; break;
        case 'CALLS_LINK':
          newCol.formatter = callsLinkFormatter; break;
        case 'DAY':
          newCol.formatter = dateFormatter; break;
        case 'DATE':
          newCol.formatter = datetimeFormatter; break;
        case 'DATE_SMALL':
          newCol.formatter = datetimeSmallFormatter; break;
        case 'DURATION':
          newCol.formatter = durationFormatter; break;
        case 'SCORE':
          newCol.hozAlign = 'right';
          newCol.formatter = scoreFormatter; break;
        case 'END_DAY':
          newCol.formatter = endDateFormatter; break;
        case 'END_DATE':
          newCol.formatter = endDatetimeFormatter; break;
        case 'END_DATE_SMALL':
          newCol.formatter = endTimeSmallFormatter; break;
        case 'ADR_DOCUMENT_LINK':
          if (!readOnly && col.clickCallback) {
            newCol.cellClick = function (e,cell) {
              if (e.currentTarget && window[col.clickCallback] && cell.getElement().querySelector('a').contains(e.target)) {
                // using window global function is a semi-horrible hack used for the enabled column renderer
                window[col.clickCallback](cell.getValue(), cell.getData()['adr_LastDoc.DocId'] || cell.getData().id);
              }
            };
          }
          newCol.formatter = adrDocumentLinkFormatter; break;
        case 'ENDPOINT_DETAILS_LINK':
        case 'ENDPOINT_DETAILS_ID_LINK':
          newCol.formatter = endpointLinkFormatter; break;
        case 'NETWORK_LINK':
          newCol.formatter = networkLinkFormatter; break;
        case 'HOST_ADDRESS':
          newCol.formatter = hostAddressFormatter; break;
        case 'LINK':
          newCol.formatter = linkFormatter; break;
        case 'LIST':
          newCol.formatter = commaSeparatedListFormatter; break;
        case 'MEDIA_ROUTED':
          newCol.formatter = mediaRoutedFormatter; break;
        case 'MEETING_LINK':
          newCol.formatter = meetingLinkFormatter; break;
        case 'NUMBER':
          newCol.hozAlign = 'right';
          newCol.formatter = numberFormatter; break;
        case 'INTEGER':
          newCol.hozAlign = 'right';
          newCol.formatter = integerFormatter; break;
        case 'BYTES':
          newCol.hozAlign = 'right';
          newCol.formatter = bytesFormatter; break;
        case 'PERCENTAGE':
          newCol.hozAlign = 'right';
          newCol.formatter = percentageFormatter; break;
        case 'SEMANTIC_TEXT':
          newCol.formatter = semanticTextFormatter; break;
        case 'QUALITY':
          newCol.formatter = qualityFormatter; break;
        case 'ADR_STATUS':
          newCol.formatter = adrStatusFormatter; break;
        case 'EVENT_LEVEL':
          newCol.formatter = eventLevelFormatter; break;
        case 'STREAM_TYPE':
          newCol.formatter = streamTypeFormatter; break;
        case 'STATUS':
          newCol.formatter = statusFormatter; break;
        case 'CALL_TYPE':
          newCol.formatter = callTypeFormatter; break;
        case 'CONNECTION':
          newCol.formatter = connectionFormatter; break;
        case 'MULTI_IP':
          newCol.formatter = multiIPFormatter; break;
        case 'MULTI_PLATFORM':
          newCol.formatter = multiPlatformFormatter; break;
        case 'MULTI_PERIPHERALS':
          newCol.formatter = multiPeripheralsFormatter; break;
        case 'MULTILINE_STRING':
          newCol.formatter = 'textarea'; break;
        case 'ALERT_STATE':
          newCol.sorter = function(a, b, aRow, bRow, column, dir, sorterParams){
            function convertToInt(value) {
              switch (value.toLowerCase()) {
                case 'critical':
                  return 4;
                case 'warn':
                  return 3;
                case 'info':
                  return 2;
                case 'clear':
                  return 1;
                default:
                  return 0;
              }
            }
            return convertToInt(a) - convertToInt(b);
          };
          newCol.formatter = alertStateFormatter; break;
        case 'GROUP_NICE_NAME':
          newCol.sorter = groupNiceNameSorter;
          newCol.formatter = groupNiceNameFormatter; break;
        case 'EVENT_MESSAGE':
          newCol.formatter = eventMessageFormatter; break;
        case 'EVENT_ORIGIN_NAME':
          newCol.formatter = eventOriginNameFormatter; break;
        case 'METRIC_DISPLAY':
          newCol.formatter = metricDisplayFormatter; break;
        case 'CATEGORY_DISPLAY':
          newCol.formatter = categoryDisplayFormatter; break;
        case 'CREATED_BY_DISPLAY':
          newCol.formatter = createdByDisplayFormatter; break;
        case 'EDITABLE_BOOLEAN':
          if (!readOnly && col.clickCallback) {
            newCol.cellClick = function (e,cell) {
              if (e.currentTarget && window[col.clickCallback] && cell.getElement().querySelector('.legend-slider').contains(e.target)) {
                // using window global function is a semi-horrible hack used for the enabled column renderer
                window[col.clickCallback](cell.getColumn().getField(), cell.getElement().querySelector('input'), cell.getValue(), cell.getData().alertId);
              }
            };
          }
          newCol.formatter = readOnly ? enabledFormatter: editableEnabledFormatter; break; // todo UI-2282
        case 'EDIT_ALERT_ACTION':
          newCol.formatter = alertDetailsEditFormatter; break;
        case 'VIEW_ALERT':
          newCol.formatter = alertDetailsViewFormatter; break;
        case 'MOS':
          newCol.formatter = mosFormatter; break;
        case 'FILTER_DESCRIPTION':
          newCol.formatter = defaultFormatter;
          newCol.sorter = function(a, b, aRow, bRow, column, dir, sorterParams) { // trim parans from start and end before sort alphabetical
            return this.table.modules.sort.sorters['string'](a ? a.replace(/^\(+|\)+$/g, '') : a,
                b ? b.replace(/^\(+|\)+$/g, '') : b,
                aRow, bRow, column, dir, sorterParams);
          }; break;
        case 'CAMERA': // todo UI-2130
        case 'MICROPHONE': // todo UI-2130
        case 'SPEAKER': // todo UI-2130
          newCol.formatter = peripheralsFormatter; break;
        case 'STAR_RATING': // todo UI-2130
          newCol.formatter = starRatingFormatter; break;
        case 'TRUNK_LINK': // todo UI-2130
        case 'SYSTEM_LINK': // todo UI-2130
        case 'RAW_NUMBER':
        default:
          newCol.formatter = defaultFormatter;
      }

      tabulatorColumns.push(newCol);
    });
    return tabulatorColumns;
  },

  /**
   *
   * @param {String} datasetName is assumed to be a string. If it is a knockout observable, unwrap it before calling this function
   * @param {Object} params query params for the ajax request
   */
  getDataset: function(datasetName, params = {}) {
    return DataServiceSecure.fetchDataset(datasetName, params);
  },

  getParticipantsDataset: function(meetingId, startTime) {
    return DataServiceSecure.fetchMeetingParticipants({ meetingId, startTime });
  },

  getColumnsFromDatasetMetadata: function(datasetMetadata, readOnly) {
    return this.convertColumns(datasetMetadata.columns, undefined, readOnly);
  },

  getColumnsFromMetadata: function(datasetName, metadata, readOnly) {
    let datasetMetadata = metadata.find(config => config.id.toLowerCase() === datasetName.toLowerCase());
    return this.getColumnsFromDatasetMetadata(datasetMetadata, readOnly);
  },

  getInvisibleColumnsFromDatasetMetadata: function(datasetMetadata) {
    return _.map(_.filter(datasetMetadata.columns, col => col.invisible), col => col.id);
  },

  getInvisibleColumnsFromMetadata: function(datasetName, metadata) {
    let datasetMetadata = metadata.find(config => config.id.toLowerCase() === datasetName.toLowerCase());
    return this.getInvisibleColumnsFromDatasetMetadata(datasetMetadata);
  },

  getChildColumnsFromMetadata: function(datasetName, metadata) {
    let childColumns = [];
    let childDatasetName = CHILD_DATASET_MAP[datasetName];
    if (childDatasetName) {
      let childDatasetMetadata = metadata.find(config => config.id.toLowerCase() === childDatasetName.toLowerCase());
      childColumns = this.convertColumns(childDatasetMetadata.columns);
    }
    return childColumns;
  },

  getLocalSortFromMetadata: function(datasetName, metadata) {
    let datasetMetadata = metadata.find(config => config.id.toLowerCase() === datasetName.toLowerCase());
    return datasetMetadata.localSort || false;
  },

  getLocalFilterFromMetadata: function(datasetName, metadata) {
    let datasetMetadata = metadata.find(config => config.id.toLowerCase() === datasetName.toLowerCase());
    return datasetMetadata.localFilter || false;
  },

  getNoPaginationFromMetadata: function(datasetName, metadata) {
    let datasetMetadata = metadata.find(config => config.id.toLowerCase() === datasetName.toLowerCase());
    return datasetMetadata.noPagination || false;
  },

  getDataIndexFromMetadata: function(datasetName, metadata) {
    let datasetMetadata = metadata.find(config => config.id.toLowerCase() === datasetName.toLowerCase());
    return datasetMetadata.indexColumn;
  },

  getDisplayNameFromMetadata: function(datasetName, metadata) {
    let datasetMetadata = metadata.find(config => config.id.toLowerCase() === datasetName.toLowerCase());
    return datasetMetadata.nameColumn;
  },

  getDataSourcesConfigFromColumns: function(columns, dataSourcesMetadata) {
    return dataSourcesMetadata.map(dataSource => {
      let dataSourceCopy = Object.assign({}, dataSource);
      dataSourceCopy.options = [];
      columns.forEach(col => {
        if (col.applicableSourcesInclude && col.applicableSourcesInclude.includes(dataSourceCopy.id)) {
          dataSourceCopy.options.push(col.id);
        } else if (col.applicableSourcesExclude && !col.applicableSourcesExclude.includes(dataSourceCopy.id)) {
          dataSourceCopy.options.push(col.id);
        }
      });
      return dataSourceCopy;
    });
  },

  getDataSourcesConfig: function(datasetName, datasetMetadata, dataSourcesMetadata) {
    let columns = datasetMetadata.columns;
    return dataSourcesMetadata.map(dataSource => {
      let dataSourceCopy = Object.assign({}, dataSource);
      dataSourceCopy.options = [];
      columns.forEach(col => {
        if (col.applicableSourcesInclude && col.applicableSourcesInclude.includes(dataSourceCopy.id)) {
          dataSourceCopy.options.push(col.id);
        } else if (col.applicableSourcesExclude && !col.applicableSourcesExclude.includes(dataSourceCopy.id)) {
          dataSourceCopy.options.push(col.id);
        }
      });
      return dataSourceCopy;
    });
  },

  getCategoriesConfigFromDatasetMetadata: function(datasetMetadata) {
    return datasetMetadata.categories;
  },
  /**
   *
   * @param {Array} metadata The metadata to parse to extract the categories config.
   *                         This must be the raw metadata that includes categories,
   *                         retrieved from metadata.service.js fetchDatasetMetadata
   */
  getCategoriesConfig: function(datasetName, metadata) {
    let datasetMetadata = metadata.find(config => config.id.toLowerCase() === datasetName.toLowerCase());
    return datasetMetadata.categories;
  },

  getSortParam: function(sorters) {
    let sort = '';
    for (let i = 0, _i = sorters.length; i < _i; i++) {
      if (sorters[i].dir !== 'none') {
        if (i > 0) { sort += '&sort='; }
        sort += sorters[i].field + ',' + sorters[i].dir;
      }
    }
    return sort;
  },

  getDataSortParam: function(sorters) {
    let sort = '';
    for (let i = 0, _i = sorters.length; i < _i; i++) {
      if (sorters[i].dir !== 'none') {
        if (i > 0) { sort += ', '; }
        sort += sorters[i].field + ' ' + sorters[i].dir.toUpperCase();
      }
    }
    return sort;
  },

  getFilterString: function(filters) {
    let filterString = '';
    filters.forEach((filter, index) => {
      if (index > 0) { filterString += ';' }
      filterString += filter.value;
    });
    return filterString;
  },

  addSortingToColumns: function(columns, sortModel) {
    return columns.map(col => {
      sortModel.forEach(sorter => {
        if (col.field === sorter.colId && !!sorter.sort) {
          col.sort = sorter.sort;
        } else {
          delete col.sort; // the table will try to sort on anything with the property so delete it
        }
      });
      return col;
    });
  },

  /*
  Returns either the quick start filter configuration or the default filter group with no filters applied.
  We add a custom "isQuickStart" boolean to the filters so that filter.expression.service knows an empty value is valid.
*/
  getQuickStartFilterGroups: function (datasetName, metadata) {
  if (datasetName === 'calls') {
    let dataSource = metadata.columns.find(col => col.id === 'data_source');
    let systemName = metadata.columns.find(col => col.id === 'system_name');
    let quality = metadata.columns.find(col => col.id === 'avg_quality');
    let callType = metadata.columns.find(col => col.id === 'call_type');
    let connectionType = metadata.columns.find(col => col.id === 'connection_type');
    return [{
      "operator": "and",
      "groupId": 0,
      "filters": [{
        "id": 0,
        "field": quality.id,
        "fieldText": quality.name,
        "type": "string",
        "filterComplete": true,
        "knownValuePath": undefined,
        "operatorText": "is",
        "operatorValue": "quickPick",
        "isQuickPick": true,
        "widget":"toggles",
        "isQuickStart": true,
        "quickPickValues": ["Good", "Fair", "Bad", "Unknown"]
      },
        {
          "id": 1,
          "field": callType.id,
          "fieldText": callType.name,
          "type": "string",
          "filterComplete": true,
          "knownValuePath": undefined,
          "operatorText": "is",
          "operatorValue": "quickPick",
          "isQuickPick": true,
          "widget":"toggles",
          "isQuickStart": true,
          "quickPickValues": ["Video", "Audio", "Other"]
        },
        {
          "id": 2,
          "field": connectionType.id,
          "fieldText": connectionType.name,
          "type": "string",
          "filterComplete": true,
          "knownValuePath": undefined,
          "operatorText": "is",
          "operatorValue": "quickPick",
          "isQuickPick": true,
          "widget":"toggles",
          "isQuickStart": true,
          "quickPickValues": ["Wired", "Wifi", "Other"]
        },
        {
          "id": 3,
          "field": dataSource.id,
          "fieldText": dataSource.name,
          "type": "string",
          "filterComplete": true,
          "knownValuePath": dataSource.knownValuePath,
          "operatorText": "in",
          "operatorValue": "=in=(|)",
          "isQuickPick": false,
          "isQuickStart": true,
          "quickPickValues": undefined
        },
        {
          "id": 4,
          "field": systemName.id,
          "fieldText": systemName.name,
          "type": "string",
          "filterComplete": true,
          "knownValuePath": systemName.knownValuePath,
          "operatorText": "in",
          "operatorValue": "=in=(|)",
          "isQuickPick": false,
          "isQuickStart": true,
          "quickPickValues": undefined
        }
      ]
    }];
  }

  if (datasetName === 'endpoints') {
    let name = metadata.columns.find(col => col.id === 'name');
    let status = metadata.columns.find(col => col.id === 'status');
    // let camera = metadata.columns.find(col => col.id === 'camera');
    // let display = metadata.columns.find(col => col.id === 'display');
    // let microphone = metadata.columns.find(col => col.id === 'microphone');
    // let touchPanel = metadata.columns.find(col => col.id === 'touch_panel');
    return [{
      "operator": "and",
      "groupId": 0,
      "filters": [{
        "id": 0,
        "field": "name",
        "fieldText": "Endpoint Name",
        "type": "string",
        "filterComplete": true,
        "operatorText": "contains",
        "operatorValue": "==\"*|*\"",
        "isQuickPick": false,
        "isQuickStart": true,
        "queryVar": "",
        "value": "",
        "text": ""
      },
        {
        "id": 1,
        "field": status.id,
        "fieldText": status.name,
        "type": "string",
        "filterComplete": true,
        "knownValuePath": undefined,
        "operatorText": "is",
        "operatorValue": "quickPick",
        "isQuickPick": true,
        "widget":"toggles",
        "isQuickStart": true,
        "quickPickValues": ["Available", "None", "NotAvailable", "InCall"]
      }]
    },
      // {
      //   "operator": "or",
      //   "groupId": 1,
      //   "filters": [{
      //     "id": 0,
      //     "field": camera.id,
      //     "fieldText": camera.name,
      //     "type": "string",
      //     "filterComplete": true,
      //     "knownValuePath": undefined,
      //     "operatorText": "is",
      //     "operatorValue": "quickPick",
      //     "isQuickPick": true,
      //     "widget":"toggles",
      //     "isQuickStart": true,
      //     "quickPickValues": ["Good", "Bad", "Neutral"]
      //   },
      //     {
      //       "id": 1,
      //       "field": display.id,
      //       "fieldText": display.name,
      //       "type": "string",
      //       "filterComplete": true,
      //       "knownValuePath": undefined,
      //       "operatorText": "is",
      //       "operatorValue": "quickPick",
      //       "isQuickPick": true,
      //       "widget":"toggles",
      //       "isQuickStart": true,
      //       "quickPickValues": ["Good", "Bad", "Neutral"]
      //     },
      //     {
      //       "id": 2,
      //       "field": microphone.id,
      //       "fieldText": microphone.name,
      //       "type": "string",
      //       "filterComplete": true,
      //       "knownValuePath": undefined,
      //       "operatorText": "is",
      //       "operatorValue": "quickPick",
      //       "isQuickPick": true,
      //       "widget":"toggles",
      //       "isQuickStart": true,
      //       "quickPickValues": ["Good", "Bad", "Neutral"]
      //     },
      //     {
      //       "id": 3,
      //       "field": touchPanel.id,
      //       "fieldText": touchPanel.name,
      //       "type": "string",
      //       "filterComplete": true,
      //       "knownValuePath": undefined,
      //       "operatorText": "is",
      //       "operatorValue": "quickPick",
      //       "isQuickPick": true,
      //       "widget":"toggles",
      //       "isQuickStart": true,
      //       "quickPickValues": ["Good", "Bad", "Neutral"]
      //     }
      //   ]
      // }
  ];
  }

  if (datasetName === 'meetings') {
    let systemType = metadata.columns.find(col => col.id === 'type');
    let systemName = metadata.columns.find(col => col.id === 'system_name');
    let quality = metadata.columns.find(col => col.id === 'quality');
    return [{
      "operator": "and",
      "groupId": 0,
      "filters": [{
        "id": 0,
        "field": quality.id,
        "fieldText": quality.name,
        "type": "string",
        "filterComplete": true,
        "knownValuePath": undefined,
        "operatorText": "is",
        "operatorValue": "quickPick",
        "isQuickPick": true,
        "widget":"toggles",
        "isQuickStart": true,
        "quickPickValues": ["Good", "Fair", "Bad", "Unknown"]
      },
        {
          "id": 1,
          "field": systemType.id,
          "fieldText": systemType.name,
          "type": "string",
          "filterComplete": true,
          "knownValuePath": systemType.knownValuePath,
          "operatorText": "in",
          "operatorValue": "=in=(|)",
          "isQuickPick": false,
          "isQuickStart": true,
          "quickPickValues": undefined
        },
        {
          "id": 2,
          "field": systemName.id,
          "fieldText": systemName.name,
          "type": "string",
          "filterComplete": true,
          "knownValuePath": systemName.knownValuePath,
          "operatorText": "in",
          "operatorValue": "=in=(|)",
          "isQuickPick": false,
          "isQuickStart": true,
          "quickPickValues": undefined
        }
      ]
    }];
  }

  if (datasetName === 'systems') {
    let status = metadata.columns.find(col => col.id === 'status');
    let systemType = metadata.columns.find(col => col.id === 'type');
    let systemName = metadata.columns.find(col => col.id === 'name');
    return [{
      "operator": "and",
      "groupId": 0,
      "filters": [{
        "id": 0,
        "field": status.id,
        "fieldText": status.name,
        "type": "string",
        "filterComplete": true,
        "knownValuePath": undefined,
        "operatorText": "is",
        "operatorValue": "quickPick",
        "isQuickPick": true,
        "widget":"toggles",
        "isQuickStart": true,
        "quickPickValues": ["Ok", "None", "Unreachable", "Error"]
      },
        {
          "id": 1,
          "field": systemType.id,
          "fieldText": systemType.name,
          "type": "string",
          "filterComplete": true,
          "knownValuePath": systemType.knownValuePath,
          "operatorText": "in",
          "operatorValue": "=in=(|)",
          "isQuickPick": false,
          "isQuickStart": true,
          "quickPickValues": undefined
        },
        {
          "id": 2,
          "field": systemName.id,
          "fieldText": systemName.name,
          "type": "string",
          "filterComplete": true,
          "knownValuePath": systemName.knownValuePath,
          "operatorText": "in",
          "operatorValue": "=in=(|)",
          "isQuickPick": false,
          "isQuickStart": true,
          "quickPickValues": undefined
        }
      ]
    }];
  }

  if (datasetName === 'trunks') {
    let status = metadata.columns.find(col => col.id === 'status');
    let systemName = metadata.columns.find(col => col.id === 'system_name');
    return [{
      "operator": "and",
      "groupId": 0,
      "filters": [{
        "id": 0,
        "field": status.id,
        "fieldText": status.name,
        "type": "string",
        "filterComplete": true,
        "knownValuePath": undefined,
        "operatorText": "is",
        "operatorValue": "quickPick",
        "isQuickPick": true,
        "widget":"toggles",
        "isQuickStart": true,
        "quickPickValues": ["OK", "None", "Unreachable", "Error"]
      },
        {
          "id": 1,
          "field": systemName.id,
          "fieldText": systemName.name,
          "type": "string",
          "filterComplete": true,
          "knownValuePath": systemName.knownValuePath,
          "operatorText": "in",
          "operatorValue": "=in=(|)",
          "isQuickPick": false,
          "isQuickStart": true,
          "quickPickValues": undefined
        }
      ]
    }];
  }

  if (datasetName === 'events') {
    let source_type = metadata.columns.find(col => col.id === 'source_type');
    let event_type = metadata.columns.find(col => col.id === 'event_type');
    let event_level = metadata.columns.find(col => col.id === 'event_level'); // todo make quick pick

    return [{
      "filters": [{
        "id": 0,
        "field": source_type.id,
        "fieldText": source_type.name,
        "type": "string",
        "filterComplete": true,
        "knownValues": source_type.knownValues,
        "displayId": source_type.displayId,
        "operatorText": "in",
        "operatorValue": "=in=(|)",
        "isQuickPick": false,
        "isQuickStart": true,
        "value": "source_type=in=(\"endpoints\")",
        "text": source_type.name + " in (Endpoints)",
        "shortText": "Endpoints",
        "queryVar": "endpoints"
      }, {
        "id": 1,
        "field": event_type.id,
        "fieldText": event_type.name,
        "type": "string",
        "filterComplete": true,
        "knownValues": event_type.knownValues,
        "displayId": event_type.displayId,
        "operatorText": "in",
        "operatorValue": "=in=(|)",
        "isQuickPick": false,
        "isQuickStart": true,
        "value": "",
        "text": "",
        "queryVar": ""
      }, {
        "id": 2,
        "field": event_level.id,
        "fieldText": event_level.name,
        "type": "string",
        "filterComplete": true,
        "knownValues": event_level.knownValues,
        "displayId": event_level.displayId,
        "operatorText": "in",
        "operatorValue": "=in=(|)",
        "isQuickPick": false,
        "isQuickStart": true,
        "value": "",
        "text": "",
        "queryVar": ""
      }], 
      "operator": "and", 
      "groupId": 0
    }];
  }
  if (datasetName === 'endpoints-events') {
    let endpoint_id = metadata.columns.find(col => col.id === 'endpoint_id');
    let tags = metadata.columns.find(col => col.id === 'tags');
    return [{
      "filters": [{
        "id": 0,
        "field": endpoint_id.id,
        "fieldText": endpoint_id.name,
        "type": "number",
        "filterComplete": true,
        "operatorText": "is",
        "operatorValue": "quickPick",
        "isQuickPick": true,
        "widget":"ids",
        "data":{"selections":[]}, // todo set data on all quick pick quick starts
        "quickPickValues": undefined,
        "queryVar": "",
        "text": "",
        "isQuickStart": true
      }, {
        "id": 1,
        "field": tags.id,
        "fieldText": tags.name,
        "knownValuePath": tags.knownValuePath,
        "type": "list",
        "filterComplete": true,
        "operatorText": "contains",
        "operatorValue": "==\"*|*\"",
        "isQuickPick": false,
        "isQuickStart": true
      }], 
      "operator": "and", 
      "groupId": 0
    }];
  }
  if (datasetName === 'endpoint-monitors') {
    let endpoint_id = metadata.filterOptions.find(col => col.id === 'endpoint_id');
    return [{
      "filters": [{
        "id": 0,
        "field": endpoint_id.id,
        "fieldText": endpoint_id.name,
        "type": "metric",
        "filterComplete": true,
        "operatorText": "is",
        "operatorValue": "quickPick",
        "isQuickPick": true,
        "widget":"ids",
        "data":{"selections":[]}, // todo set data on all quick pick quick starts
        "quickPickValues": undefined,
        "queryVar": "",
        "text": "",
        "isQuickStart": true
      }], 
      "operator": "and", 
      "groupId": 0
    }];
  }
  if (datasetName === 'bridges-monitors'|| datasetName === 'callcontrol-monitors') {
    let system_id = metadata.filterOptions.find(col => col.id === 'system_id');
    return [{
      "filters": [{
        "id": 0,
        "type": "metric",
        "field": system_id.id,
        "fieldText": system_id.name,
        "isQuickPick": true,
        "operatorText": "is",
        "operatorValue": "quickPick",
        "widget":"ids",
        "data":{"selections":[]}, // todo set data on all quick pick quick starts
        "quickPickValues": undefined,
        "queryVar": "",
        "text": "",
        "isQuickStart": true
      }],
      "groupId": 0,
      "operator": "and"
    }];
  }

    if (datasetName === 'spaces') {
      let name = metadata.columns.find(col => col.id === 'van_room.name');
      let buildingName = metadata.columns.find(col => col.id === 'building.name');
      let type = metadata.columns.find(col => col.id === 'van_room.type');
      return [{
        "operator": "and",
        "groupId": 0,
        "filters": [
          {
            "id": 0,
            "field": name.id,
            "fieldText": name.name,
            "type": "string",
            "filterComplete": true,
            "operatorText": "contains",
            "operatorValue": "==\"*|*\"",
            "isQuickPick": false,
            "isQuickStart": true,
            "quickPickValues": undefined
          },
          {
            "id": 1,
            "field": buildingName.id,
            "fieldText": buildingName.name,
            "type": "string",
            "filterComplete": true,
            "operatorText": "contains",
            "operatorValue": "==\"*|*\"",
            "isQuickPick": false,
            "isQuickStart": true,
            "quickPickValues": undefined
          },
          {
            "id": 2,
            "field": type.id,
            "fieldText": type.name,
            "type": "string",
            "filterComplete": true,
            "operatorText": "contains",
            "operatorValue": "==\"*|*\"",
            "isQuickPick": false,
            "isQuickStart": true,
            "quickPickValues": undefined
          }
        ]
      }];
    }

    if (datasetName === 'buildings') {
      let name = metadata.columns.find(col => col.id === 'name');
      let city = metadata.columns.find(col => col.id === 'city');
      let country = metadata.columns.find(col => col.id === 'country');
      let region = metadata.columns.find(col => col.id === 'region');
      return [{
        "operator": "and",
        "groupId": 0,
        "filters": [
          {
            "id": 0,
            "field": name.id,
            "fieldText": name.name,
            "type": "string",
            "filterComplete": true,
            "operatorText": "contains",
            "operatorValue": "==\"*|*\"",
            "isQuickPick": false,
            "isQuickStart": true,
            "quickPickValues": undefined
          },
          {
            "id": 1,
            "field": city.id,
            "fieldText": city.name,
            "type": "string",
            "filterComplete": true,
            "operatorText": "contains",
            "operatorValue": "==\"*|*\"",
            "isQuickPick": false,
            "isQuickStart": true,
            "quickPickValues": undefined
          },
          {
            "id": 2,
            "field": country.id,
            "fieldText": country.name,
            "type": "string",
            "filterComplete": true,
            "operatorText": "contains",
            "operatorValue": "==\"*|*\"",
            "isQuickPick": false,
            "isQuickStart": true,
            "quickPickValues": undefined
          },
          {
            "id": 3,
            "field": region.id,
            "fieldText": region.name,
            "type": "string",
            "filterComplete": true,
            "operatorText": "contains",
            "operatorValue": "==\"*|*\"",
            "isQuickPick": false,
            "isQuickStart": true,
            "quickPickValues": undefined
          }
        ]
      }];
    }

    if (datasetName === 'sensors') {
      // let sensorKey = metadata.columns.find(col => col.id === 'sensorKey');
      let name = metadata.columns.find(col => col.id === 'name');
      let manufacturer = metadata.columns.find(col => col.id === 'manufacturer');
      // let model = metadata.columns.find(col => col.id === 'model');
      // let type = metadata.columns.find(col => col.id === 'type');
      let location = metadata.columns.find(col => col.id === 'location');
      // let city = metadata.columns.find(col => col.id === 'city');
      // let country = metadata.columns.find(col => col.id === 'country');
      // let building = metadata.columns.find(col => col.id === 'building');
      // let floor = metadata.columns.find(col => col.id === 'floor');
      // let roomNumber = metadata.columns.find(col => col.id === 'roomNumber');
      // let tags = metadata.columns.find(col => col.id === 'tags');
      let exchangeResourceId = metadata.columns.find(col => col.id === 'exchangeResourceId');
      // let roomName = metadata.columns.find(col => col.id === 'roomName');
      
      return [{
        "operator": "and",
        "groupId": 0,
        "filters": [
          // {
          //   "id": 0,
          //   "field": sensorKey.id,
          //   "fieldText": sensorKey.name,
          //   "type": "string",
          //   "filterComplete": true,
          //   "operatorText": "contains",
          //   "operatorValue": "==\"*|*\"",
          //   "isQuickPick": false,
          //   "isQuickStart": false,
          //   "quickPickValues": undefined
          // },
          {
            "id": 1,
            "field": name.id,
            "fieldText": name.name,
            "type": "string",
            "filterComplete": true,
            "operatorText": "contains",
            "operatorValue": "==\"*|*\"",
            "isQuickPick": false,
            "isQuickStart": true,
            "quickPickValues": undefined
          },
          {
            "id": 2,
            "field": manufacturer.id,
            "fieldText": manufacturer.name,
            "type": "string",
            "filterComplete": true,
            "operatorText": "contains",
            "operatorValue": "==\"*|*\"",
            "isQuickPick": false,
            "isQuickStart": true,
            "quickPickValues": undefined
          },
          // {
          //   "id": 3,
          //   "field": model.id,
          //   "fieldText": model.name,
          //   "type": "string",
          //   "filterComplete": true,
          //   "operatorText": "contains",
          //   "operatorValue": "==\"*|*\"",
          //   "isQuickPick": false,
          //   "isQuickStart": false,
          //   "quickPickValues": undefined
          // },
          // {
          //   "id": 4,
          //   "field": type.id,
          //   "fieldText": type.name,
          //   "type": "string",
          //   "filterComplete": true,
          //   "operatorText": "contains",
          //   "operatorValue": "==\"*|*\"",
          //   "isQuickPick": false,
          //   "isQuickStart": false,
          //   "quickPickValues": undefined
          // },
          {
            "id": 5,
            "field": location.id,
            "fieldText": location.name,
            "type": "string",
            "filterComplete": true,
            "operatorText": "contains",
            "operatorValue": "==\"*|*\"",
            "isQuickPick": false,
            "isQuickStart": true,
            "quickPickValues": undefined
          },
          // {
          //   "id": 6,
          //   "field": city.id,
          //   "fieldText": city.name,
          //   "type": "string",
          //   "filterComplete": true,
          //   "operatorText": "contains",
          //   "operatorValue": "==\"*|*\"",
          //   "isQuickPick": false,
          //   "isQuickStart": false,
          //   "quickPickValues": undefined
          // },
          // {
          //   "id": 7,
          //   "field": country.id,
          //   "fieldText": country.name,
          //   "type": "string",
          //   "filterComplete": true,
          //   "operatorText": "contains",
          //   "operatorValue": "==\"*|*\"",
          //   "isQuickPick": false,
          //   "isQuickStart": false,
          //   "quickPickValues": undefined
          // },
          // {
          //   "id": 8,
          //   "field": building.id,
          //   "fieldText": building.name,
          //   "type": "string",
          //   "filterComplete": true,
          //   "operatorText": "contains",
          //   "operatorValue": "==\"*|*\"",
          //   "isQuickPick": false,
          //   "isQuickStart": false,
          //   "quickPickValues": undefined
          // },
          // {
          //   "id": 9,
          //   "field": floor.id,
          //   "fieldText": floor.name,
          //   "type": "string",
          //   "filterComplete": true,
          //   "operatorText": "contains",
          //   "operatorValue": "==\"*|*\"",
          //   "isQuickPick": false,
          //   "isQuickStart": false,
          //   "quickPickValues": undefined
          // },
          // {
          //   "id": 10,
          //   "field": roomNumber.id,
          //   "fieldText": roomNumber.name,
          //   "type": "string",
          //   "filterComplete": true,
          //   "operatorText": "contains",
          //   "operatorValue": "==\"*|*\"",
          //   "isQuickPick": false,
          //   "isQuickStart": false,
          //   "quickPickValues": undefined
          // },
          // {
          //   "id": 11,
          //   "field": tags.id,
          //   "fieldText": tags.name,
          //   "type": "string",
          //   "filterComplete": true,
          //   "operatorText": "contains",
          //   "operatorValue": "==\"*|*\"",
          //   "isQuickPick": false,
          //   "isQuickStart": false,
          //   "quickPickValues": undefined
          // },
          {
            "id": 12,
            "field": exchangeResourceId.id,
            "fieldText": exchangeResourceId.name,
            "type": "string",
            "filterComplete": true,
            "operatorText": "contains",
            "operatorValue": "==\"*|*\"",
            "isQuickPick": false,
            "isQuickStart": true,
            "quickPickValues": undefined
          },
          // {
          //   "id": 13,
          //   "field": roomName.id,
          //   "fieldText": roomName.name,
          //   "type": "string",
          //   "filterComplete": true,
          //   "operatorText": "contains",
          //   "operatorValue": "==\"*|*\"",
          //   "isQuickPick": false,
          //   "isQuickStart": false,
          //   "quickPickValues": undefined
          // }
        ]
      }];
    }


    if (datasetName === 'networks') {
      let ip = metadata.columns.find(col => col.id === 'network.ip');
      let name = metadata.columns.find(col => col.id === 'network.name');
      let buildingName = metadata.columns.find(col => col.id === 'building.name');
      let city = metadata.columns.find(col => col.id === 'building.city');
      let country = metadata.columns.find(col => col.id === 'building.country');

      return [{
        "operator": "and",
        "groupId": 0,
        "filters": [
          {
            "id": 0,
            "field": ip.id,
            "fieldText": ip.name,
            "type": "string",
            "filterComplete": true,
            "operatorText": "contains",
            "operatorValue": "==\"*|*\"",
            "isQuickPick": false,
            "isQuickStart": true,
            "quickPickValues": undefined
          },
          {
            "id": 1,
            "field": name.id,
            "fieldText": name.name,
            "type": "string",
            "filterComplete": true,
            "operatorText": "contains",
            "operatorValue": "==\"*|*\"",
            "isQuickPick": false,
            "isQuickStart": true,
            "quickPickValues": undefined
          },
          {
            "id": 2,
            "field": buildingName.id,
            "fieldText": buildingName.name,
            "type": "string",
            "filterComplete": true,
            "operatorText": "contains",
            "operatorValue": "==\"*|*\"",
            "isQuickPick": false,
            "isQuickStart": true,
            "quickPickValues": undefined
          },
          {
            "id": 1,
            "field": city.id,
            "fieldText": city.name,
            "type": "string",
            "filterComplete": true,
            "operatorText": "contains",
            "operatorValue": "==\"*|*\"",
            "isQuickPick": false,
            "isQuickStart": true,
            "quickPickValues": undefined
          },
          {
            "id": 2,
            "field": country.id,
            "fieldText": country.name,
            "type": "string",
            "filterComplete": true,
            "operatorText": "contains",
            "operatorValue": "==\"*|*\"",
            "isQuickPick": false,
            "isQuickStart": true,
            "quickPickValues": undefined
          }
        ]
      }];
    }

    if (datasetName === 'documents') {
      let id = metadata.columns.find(col => col.id === 'id');
      let name = metadata.columns.find(col => col.id === 'name');
      let systemId = metadata.columns.find(col => col.id === 'systemId');
      let systemType = metadata.columns.find(col => col.id === 'systemType');
      let docType = metadata.columns.find(col => col.id === 'docType');
      let status = metadata.columns.find(col => col.id === 'status');

      return [
        {
          "filters": [
            {
              "id": 0,
              "field": id.id,
              "fieldText": id.name,
              "type": "number",
              "filterComplete": true,
              "operatorText": "greater than",
              "operatorValue": "=gt=",
              "isQuickPick": false,
              "value": "",
              "text": "",
              "shortText": ""
            },
            {
              "id": 1,
              "field": name.id,
              "fieldText": name.name,
              "type": "string",
              "filterComplete": true,
              "operatorText": "contains",
              "operatorValue": "==\"*|*\"",
              "isQuickPick": false,
              "value": "",
              "text": "",
              "shortText": ""
            },
            {
              "id": 2,
              "field": systemId.id,
              "fieldText": systemId.name,
              "type": "number",
              "filterComplete": true,
              "operatorText": "equals",
              "operatorValue": "==",
              "isQuickPick": false,
              "value": "",
              "text": "",
              "shortText": ""
            },
            {
              "id": 3,
              "field": systemType.id,
              "fieldText": systemType.name,
              "type": "string",
              "filterComplete": true,
              "operatorText": "contains",
              "operatorValue": "==\"*|*\"",
              "isQuickPick": false,
              "value": "",
              "text": "",
              "shortText": ""
            },
            {
              "id": 4,
              "field": docType.id,
              "fieldText": docType.name,
              "type": "string",
              "filterComplete": true,
              "knownValues": docType.knownValues,
              "operatorText": "in",
              "operatorValue": "=in=(|)",
              "isQuickPick": false,
              "value": "",
              "text": "",
              "shortText": ""
            // },
            // {
            //   "id": 5,
            //   "field": status.id,
            //   "fieldText": status.name,
            //   "type": "string",
            //   "filterComplete": true,
            //   "operatorText": "in",
            //   "operatorValue": "=in=(|)",
            //   "isQuickPick": false,
            //   "value": "",
            //   "queryVar": "",
            //   "text": "",
            //   "shortText": ""
            }
          ],
          "operator": "and",
          "groupId": 0
        }
      ];
    }
    if (datasetName === 'all-documents') {
      let id = metadata.columns.find(col => col.id === 'id');
      let orgID = metadata.columns.find(col => col.id === 'orgID');
      let name = metadata.columns.find(col => col.id === 'name');
      let systemId = metadata.columns.find(col => col.id === 'systemId');
      let systemType = metadata.columns.find(col => col.id === 'systemType');
      let docType = metadata.columns.find(col => col.id === 'docType');
      let status = metadata.columns.find(col => col.id === 'status');

      return [
        {
          "filters": [
            {
              "id": 0,
              "field": id.id,
              "fieldText": id.name,
              "type": "number",
              "filterComplete": true,
              "operatorText": "greater than",
              "operatorValue": "=gt=",
              "isQuickPick": false,
              "value": "",
              "text": "",
              "shortText": ""
            },
            {
              "id": 1,
              "field": orgID.id,
              "fieldText": orgID.name,
              "type": "number",
              "filterComplete": true,
              "operatorText": "equals",
              "operatorValue": "==",
              "isQuickPick": false,
              "value": "",
              "text": "",
              "shortText": ""
            },
            {
              "id": 2,
              "field": name.id,
              "fieldText": name.name,
              "type": "string",
              "filterComplete": true,
              "operatorText": "contains",
              "operatorValue": "==\"*|*\"",
              "isQuickPick": false,
              "value": "",
              "text": "",
              "shortText": ""
            },
            {
              "id": 3,
              "field": systemId.id,
              "fieldText": systemId.name,
              "type": "number",
              "filterComplete": true,
              "operatorText": "equals",
              "operatorValue": "==",
              "isQuickPick": false,
              "value": "",
              "text": "",
              "shortText": ""
            },
            {
              "id": 4,
              "field": systemType.id,
              "fieldText": systemType.name,
              "type": "string",
              "filterComplete": true,
              "operatorText": "contains",
              "operatorValue": "==\"*|*\"",
              "isQuickPick": false,
              "value": "",
              "text": "",
              "shortText": ""
            },
            {
              "id": 5,
              "field": docType.id,
              "fieldText": docType.name,
              "type": "string",
              "filterComplete": true,
              "knownValues": docType.knownValues,
              "operatorText": "in",
              "operatorValue": "=in=(|)",
              "isQuickPick": false,
              "value": "",
              "text": "",
              "shortText": ""
            // },
            // {
            //   "id": 5,
            //   "field": status.id,
            //   "fieldText": status.name,
            //   "type": "string",
            //   "filterComplete": true,
            //   "operatorText": "in",
            //   "operatorValue": "=in=(|)",
            //   "isQuickPick": false,
            //   "value": "",
            //   "queryVar": "",
            //   "text": "",
            //   "shortText": ""
            }
          ],
          "operator": "and",
          "groupId": 0
        }
      ];
    }
    if (datasetName === 'last-documents') {
      let orgID = metadata.columns.find(col => col.id === 'adr_LastDoc.orgID');
      let systemId = metadata.columns.find(col => col.id === 'adr_LastDoc.systemId');
      let systemType = metadata.columns.find(col => col.id === 'adr_document.systemType');
      let docType = metadata.columns.find(col => col.id === 'adr_LastDoc.docType');

      return [
        {
          "filters": [
            {
              "id": 0,
              "field": orgID.id,
              "fieldText": orgID.name,
              "type": "number",
              "filterComplete": true,
              "operatorText": "equals",
              "operatorValue": "==",
              "isQuickPick": false,
              "value": "",
              "text": "",
              "shortText": ""
            },
            {
              "id": 1,
              "field": systemId.id,
              "fieldText": systemId.name,
              "type": "number",
              "filterComplete": true,
              "operatorText": "equals",
              "operatorValue": "==",
              "isQuickPick": false,
              "value": "",
              "text": "",
              "shortText": ""
            },
            {
              "id": 2,
              "field": systemType.id,
              "fieldText": systemType.name,
              "type": "string",
              "filterComplete": true,
              "operatorText": "contains",
              "operatorValue": "==\"*|*\"",
              "isQuickPick": false,
              "value": "",
              "text": "",
              "shortText": ""
            },
            {
              "id": 3,
              "field": docType.id,
              "fieldText": docType.name,
              "type": "string",
              "filterComplete": true,
              "knownValues": docType.knownValues,
              "operatorText": "in",
              "operatorValue": "=in=(|)",
              "isQuickPick": false,
              "value": "",
              "text": "",
              "shortText": ""
            }
          ],
          "operator": "and",
          "groupId": 0
        }
      ];
    }
    
    return FilterExpressionService.getDefaultFilterExpression();
},

parseColumns: function(datasetName, columns) {
  return columns;
  return columns.map(function(column) {
    if (datasetName.toLowerCase().endsWith('xml')) {
      return {
        
      };
    }
    return structuredClone(column);
  });
},

};
